import { useEffect } from 'react';
import { attachPlanBoardTabSync } from 'collections/plan-board';
import { attachProjectTabSync } from 'collections/project';
import { closeTabSync, setTabSyncIdentity } from 'collections/tab-sync';
import { attachTaskTabSync } from 'collections/tasks/tab-sync';
import { useAccountSafe } from './use-account';
import { useUserSafe } from './use-user';
/**
 * Mount cross-tab state-db sync for the session. Call ONCE, at the app root.
 *
 * Identity is published before any attach so the very first outbound envelope
 * already carries the right `accountId`/`userId` — a message sent with null
 * identity would be dropped by every peer.
 *
 * No longer behind a prerelease flag. The remaining condition is a correctness
 * guard, not a rollout one: until the session's principal is known, an outbound
 * envelope would be dropped by every peer anyway, and accepting inbound data
 * before we know who we are would defeat the principal check that isolates
 * accounts.
 */ export const useStateDbTabSync = ()=>{
    var _ref, _ref1;
    var _user_user;
    const account = useAccountSafe();
    const user = useUserSafe();
    const accountId = (_ref = account === null || account === void 0 ? void 0 : account.accountId) !== null && _ref !== void 0 ? _ref : null;
    // `UserProfileInfo` declares a top-level `userId`, but the API does not
    // populate it — the real id lives on the nested `user`, which is why callers
    // across the app destructure `const { user } = useUser()`. Trusting the
    // declared field left identity permanently null and the sync never attached.
    const userId = (_ref1 = user === null || user === void 0 ? void 0 : (_user_user = user.user) === null || _user_user === void 0 ? void 0 : _user_user.userId) !== null && _ref1 !== void 0 ? _ref1 : null;
    useEffect(()=>{
        setTabSyncIdentity({
            accountId,
            userId
        });
    }, [
        accountId,
        userId
    ]);
    useEffect(()=>{
        // Wait for a real session: envelopes sent while unidentified are dropped on
        // receipt, and accepting peer data before we know who we are would defeat
        // the principal guard.
        if (accountId == null || userId == null) {
            return undefined;
        }
        setTabSyncIdentity({
            accountId,
            userId
        });
        const detachers = [
            attachTaskTabSync(),
            attachProjectTabSync(),
            attachPlanBoardTabSync()
        ];
        return ()=>{
            detachers.forEach((detach)=>detach());
            // Drop the channel on logout / account switch so a stale peer cannot keep
            // talking to the previous principal's tab.
            closeTabSync();
        };
    }, [
        accountId,
        userId
    ]);
};
